import React, { useEffect, useMemo, useRef } from 'react'
import {
    Box,
    CircularProgress,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { loadDocuSealScript, normalizeDocuSealHost } from './docusealEmbed'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        minHeight: 560,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
    },
    rootFullHeight: {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        padding: 0,
        border: 'none',
        borderRadius: 0,
        backgroundColor: 'transparent',
    },
    helperText: {
        flexShrink: 0,
    },
    embedHost: {
        minHeight: 520,
        flex: 1,
    },
    embedHostFullHeight: {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        '& docuseal-builder': {
            display: 'block',
            height: '100%',
            width: '100%',
        },
    },
    centered: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 240,
    },
}))

interface DocuSealBuilderEmbedProps {
    host: string
    token: string
    onSave?: (payload: { templateId: number; slug?: string }) => void
    isLoading?: boolean
    error?: string | null
    /** When false, DocuSeal only saves when the user clicks Save in the builder UI. Default true. */
    autosave?: boolean
    fullHeight?: boolean
    /** Minimal chrome — no helper text, tight padding (signable doc builder viewport). */
    compact?: boolean
    hideSendButton?: boolean
    hideSignYourselfButton?: boolean
    helperText?: string | null
    /** Pre-defined EZOS prefill fields shown in the builder Custom tab. */
    defaultFields?: Array<{ name: string; type: string; role: string }>
    /** Comma-separated DocuSeal roles; derived from defaultFields when omitted. */
    submitterRoles?: string
    /**
     * Read-only template view with field boxes labeled by name (for mapping UX).
     * Hides edit chrome; onSave is not used.
     */
    previewOnly?: boolean
}

const BUILDER_ROLE_ORDER = ['Applicant', 'Property', 'Guarantor'] as const

/** Parties the embedded builder offers. Send matches these names (and a few aliases). */
export const DOCUSEAL_BUILDER_SUBMITTER_ROLES = BUILDER_ROLE_ORDER.join(',')

function commaSeparatedRolesFromFields(
    fields: Array<{ role: string }>,
): string {
    const seen = new Set<string>()
    for (const field of fields) {
        if (field.role) {
            seen.add(field.role)
        }
    }
    const ordered: string[] = BUILDER_ROLE_ORDER.filter((role) =>
        seen.has(role),
    )
    seen.forEach((role) => {
        if (!ordered.includes(role)) {
            ordered.push(role)
        }
    })
    return ordered.join(',')
}

function extractTemplateId(detail: Record<string, unknown>): number | null {
    const candidates = [
        detail.id,
        detail.template_id,
        (detail.template as Record<string, unknown> | undefined)?.id,
    ]
    for (const value of candidates) {
        if (typeof value === 'number') return value
        if (typeof value === 'string' && value.trim()) return Number(value)
    }
    return null
}

export const DocuSealBuilderEmbed: React.FC<DocuSealBuilderEmbedProps> = ({
    host,
    token,
    onSave,
    isLoading,
    error,
    autosave = true,
    fullHeight = false,
    compact = false,
    hideSendButton = false,
    hideSignYourselfButton = false,
    helperText,
    defaultFields,
    submitterRoles,
    previewOnly = false,
}) => {
    const classes = useStyles()
    const hostRef = useRef<HTMLDivElement>(null)
    const onSaveRef = useRef(onSave)
    const [scriptReady, setScriptReady] = React.useState(false)
    const [scriptError, setScriptError] = React.useState<string | null>(null)

    const docuSealHost = normalizeDocuSealHost(host)
    const effectiveAutosave = previewOnly ? false : autosave
    const effectiveHideSend = previewOnly || hideSendButton
    const effectiveHideSignYourself = previewOnly || hideSignYourselfButton

    const builderRoles = useMemo(() => {
        if (submitterRoles) {
            return submitterRoles
        }
        if (defaultFields?.length) {
            return commaSeparatedRolesFromFields(defaultFields)
        }
        return BUILDER_ROLE_ORDER.join(',')
    }, [defaultFields, submitterRoles])

    useEffect(() => {
        onSaveRef.current = onSave
    }, [onSave])

    useEffect(() => {
        if (!docuSealHost) {
            setScriptReady(false)
            setScriptError(null)
            return
        }

        let cancelled = false
        setScriptReady(false)
        setScriptError(null)

        loadDocuSealScript(docuSealHost, 'builder')
            .then(() => {
                if (!cancelled) setScriptReady(true)
            })
            .catch(() => {
                if (!cancelled) {
                    setScriptError('Could not load DocuSeal builder script')
                }
            })

        return () => {
            cancelled = true
        }
    }, [docuSealHost])

    const defaultFieldsJson = defaultFields?.length
        ? JSON.stringify(defaultFields)
        : ''

    useEffect(() => {
        if (!scriptReady || !docuSealHost || !token || !hostRef.current) {
            return
        }

        const container = hostRef.current
        container.innerHTML = ''

        const builder = document.createElement('docuseal-builder')
        builder.setAttribute('data-host', docuSealHost)
        builder.setAttribute('data-token', token)
        builder.style.display = 'block'
        builder.style.width = '100%'
        builder.style.height = '100%'

        if (!effectiveAutosave) {
            builder.setAttribute('data-autosave', 'false')
        }
        if (effectiveHideSend) {
            builder.setAttribute('data-with-send-button', 'false')
        }
        if (effectiveHideSignYourself) {
            builder.setAttribute('data-with-sign-yourself-button', 'false')
        }
        builder.setAttribute('data-with-upload-button', 'false')
        builder.setAttribute('data-with-documents-list', 'false')
        builder.setAttribute('data-with-title', 'false')

        // Field names in boxes (vs type icons) — helps mapping and placement.
        builder.setAttribute('data-with-field-placeholder', 'true')

        if (previewOnly) {
            builder.setAttribute('data-preview', 'true')
            // Mapping table is beside us; hide DocuSeal's field list chrome.
            builder.setAttribute('data-with-fields-list', 'false')
            builder.setAttribute('data-with-fields-detection', 'false')
        } else {
            builder.setAttribute('data-with-fields-detection', 'true')
            if (builderRoles) {
                builder.setAttribute('data-roles', builderRoles)
            }
            if (defaultFields?.length) {
                builder.setAttribute(
                    'data-fields',
                    JSON.stringify(defaultFields),
                )
                builder.setAttribute('data-with-custom-fields-tab', 'true')
            }
        }

        const handleSave = (event: Event) => {
            if (previewOnly || !onSaveRef.current) {
                return
            }
            const customEvent = event as CustomEvent<Record<string, unknown>>
            const detail = customEvent.detail ?? {}
            const templateId = extractTemplateId(detail)
            if (templateId == null || Number.isNaN(templateId)) {
                return
            }
            const slug =
                (detail.slug as string | undefined) ??
                ((detail.template as Record<string, unknown> | undefined)
                    ?.slug as string | undefined)
            onSaveRef.current({ templateId, slug })
        }

        builder.addEventListener('save', handleSave)
        container.appendChild(builder)

        return () => {
            builder.removeEventListener('save', handleSave)
        }
    }, [
        scriptReady,
        docuSealHost,
        token,
        effectiveAutosave,
        effectiveHideSend,
        effectiveHideSignYourself,
        defaultFieldsJson,
        builderRoles,
        previewOnly,
    ])

    if (isLoading || !scriptReady) {
        return (
            <Box className={classes.centered}>
                <CircularProgress size={32} />
            </Box>
        )
    }

    if (scriptError || error) {
        return (
            <Box className={classes.root}>
                <Typography color="error">
                    {scriptError ?? error ?? 'Failed to load builder'}
                </Typography>
            </Box>
        )
    }

    const defaultHelperText = previewOnly
        ? 'Field boxes show DocuSeal field names. Use the mapping table to choose where each value comes from.'
        : effectiveAutosave
        ? 'Use Autodetect Fields in the builder toolbar, then review placements. Save when finished — EZOS stores the template automatically.'
        : 'Use Autodetect Fields in the builder toolbar, then review placements. Click Save in the builder when you want to persist changes.'

    const rootClass = [
        classes.root,
        fullHeight ? classes.rootFullHeight : '',
        compact ? classes.rootFullHeight : '',
    ]
        .filter(Boolean)
        .join(' ')

    const embedClass = [
        classes.embedHost,
        fullHeight ? classes.embedHostFullHeight : '',
    ]
        .filter(Boolean)
        .join(' ')

    // Pass a string to show it; omit / null hides. previewOnly shows default when omitted.
    const showHelper =
        previewOnly || (helperText !== null && helperText !== undefined)

    return (
        <Box className={rootClass}>
            {showHelper && (
                <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    className={fullHeight ? classes.helperText : undefined}
                >
                    {helperText ?? defaultHelperText}
                </Typography>
            )}
            <div ref={hostRef} className={embedClass} />
        </Box>
    )
}
